import { useCallback, useMemo, useRef, useState } from 'react';

import { compressFile, formatFileSize, isDefined, isNotDefined } from '@enterprise/core';
import type { FileUpload } from '@enterprise/types';

import type { CompressionSummary } from '#uploader/types/compression-summary.type';
import type { QueueState } from '#uploader/types/queue-state.type';
import type { StagedFile } from '#uploader/types/staged-file.type';
import type { UploaderProps } from '#uploader/types/uploader.props';

import { DEFAULT_THRESHOLD_BYTES } from '#uploader/constants/default-threshold';
import { BUILT_IN_NON_COMPRESSIBLE } from '#uploader/constants/non-compressible';
import { INITIAL_STATE } from '#uploader/types/initial-state.type';
import { getExtension } from '#uploader/utilities/get-extension';
import { makeId } from '#uploader/utilities/make-id';
import { recomputeDuplicates } from '#uploader/utilities/recompute-duplicates';
import { resolveFilename } from '#uploader/utilities/resolve-filename';

/**
 * React hook that manages the configuration and state for a file uploader component,
 * including file staging, validation, compression, suffix handling, and upload processing.
 *
 * @param allowedExtensions - Array of allowed file extensions (without dot). If empty, all extensions are allowed.
 * @param autoCompressThresholdBytes - Minimum file size in bytes for auto-compression.
 * Files below this size are not compressed.
 * @param disabled - If true, disables all uploader interactions.
 * @param getDefaultSuffix - Optional function to generate a default suffix for each file based on its index.
 * @param nonCompressibleExtensions - Array of file extensions that should never be compressed.
 * @param onFilesReady - Callback invoked with the final list of files ready for upload after processing.
 * @param outputFilenameTemplate - Optional template or function to generate output filenames for uploaded files.
 *
 * @returns An object containing uploader state, configuration, and handler functions:
 * - acceptAttr: Value for the file input's `accept` attribute.
 * - allowedSet: Set of allowed file extensions.
 * - cancelUpload: Function to cancel an ongoing upload/compression.
 * - compressCount: Number of files that will be compressed.
 * - compressionSummary: Summary of compression results, if available.
 * - dragging: Whether files are currently being dragged over the drop zone.
 * - dropZoneBlocked: Whether the drop zone is blocked (e.g., during compression or when disabled).
 * - error: Error message, if any.
 * - handleSuffixChange: Function to update the suffix for a staged file.
 * - handleUpload: Function to start the upload/compression process.
 * - hasCompressibleFiles: Whether any staged files are eligible for compression.
 * - hasStaged: Whether any files are currently staged.
 * - inputRef: Ref for the file input element.
 * - isCancelled: Whether the upload/compression was cancelled.
 * - isCompressing: Whether files are currently being compressed.
 * - isDone: Whether the upload/compression process is complete.
 * - isIdle: Whether the uploader is idle.
 * - onDrop: Handler for file drop events.
 * - onInputChange: Handler for file input change events.
 * - processed: Number of files processed so far.
 * - progressMap: Map of file IDs to compression progress percentages.
 * - rawCount: Number of files that will not be compressed.
 * - rejectedNames: Names of files rejected during staging.
 * - removeStaged: Function to remove a staged file by ID.
 * - reset: Function to reset the uploader state and clear staged files.
 * - setDragging: Function to set the dragging state.
 * - staged: Array of currently staged files.
 * - status: Current status of the uploader ('idle', 'compressing', 'done', 'error', etc.).
 * - thresholdLabel: Human-readable label for the compression threshold.
 * - uploadBlocked: Whether the upload is blocked due to duplicate suffixes or other issues.
 *
 * @remarks
 * - Handles duplicate file detection, extension validation, and suffix uniqueness.
 * - Supports both drag-and-drop and file input selection.
 * - Provides progress tracking and error handling for compression and upload.
 */
export const useUploaderConfiguration = ({
  allowedExtensions = [],
  autoCompressThresholdBytes = DEFAULT_THRESHOLD_BYTES,
  disabled = false,
  getDefaultSuffix,
  nonCompressibleExtensions = [],
  onFilesReady,
  outputFilenameTemplate,
}: Omit<UploaderProps, 'filenameSuffixLabel' | 'showSuffixInput' | 'showUploadButton'>) => {
  const [state, setState] = useState(INITIAL_STATE);
  const [dragging, setDragging] = useState(false);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const nonCompressibleSet = useMemo(() => {
    if (nonCompressibleExtensions.length === 0) {
      return BUILT_IN_NON_COMPRESSIBLE;
    }

    return new Set([
      ...BUILT_IN_NON_COMPRESSIBLE,
      ...nonCompressibleExtensions.map((e) => {
        return e.toLowerCase();
      }),
    ]);
  }, [nonCompressibleExtensions]);

  const allowedSet = useMemo(() => {
    if (allowedExtensions.length === 0) {
      return null;
    }

    return new Set(
      allowedExtensions.map((e) => {
        return e.toLowerCase();
      })
    );
  }, [allowedExtensions]);

  const acceptAttr = useMemo(() => {
    if (isNotDefined(allowedSet)) {
      return undefined;
    }

    return [...allowedSet]
      .map((e) => {
        return `.${e}`;
      })
      .join(',');
  }, [allowedSet]);

  const patch = useCallback((partial: Partial<QueueState>) => {
    setState((prev) => {
      return { ...prev, ...partial };
    });
  }, []);

  const reset = useCallback(() => {
    setState(INITIAL_STATE);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }, []);

  const stageFiles = useCallback(
    (picked: File[] | FileList) => {
      if (disabled) {
        return;
      }

      const incoming = [...picked];

      setState((prev) => {
        const existingNames = new Set(
          prev.staged.map((stagedFile) => {
            return stagedFile.file.name;
          })
        );

        const rejected: string[] = [];
        const newEntries: StagedFile[] = [];

        for (const file of incoming) {
          if (existingNames.has(file.name)) {
            continue;
          }

          const ext = getExtension(file.name);

          if (isDefined(allowedSet) && !allowedSet.has(ext)) {
            rejected.push(file.name);
            continue;
          }

          const isNonCompressible = nonCompressibleSet.has(ext);
          const meetsThreshold = file.size >= autoCompressThresholdBytes;
          const willCompress = meetsThreshold && !isNonCompressible;

          // 1-based position within the full staged list (existing + new so far)
          const index = prev.staged.length + newEntries.length + 1;

          // Call getDefaultSuffix if provided. undefined return means fall back to
          // auto-increment for this file — isCustomSuffix tracks which path was taken.
          const defaultSuffixValue = isDefined(getDefaultSuffix) ? getDefaultSuffix(index) : undefined;
          const suffix = defaultSuffixValue ?? String(index);
          const isCustomSuffix = isDefined(defaultSuffixValue);

          newEntries.push({
            file,
            hasDuplicateSuffix: false, // recomputed below after the full list is assembled
            id: makeId(),
            isCustomSuffix,
            skipReason: willCompress ? null : isNonCompressible ? 'non-compressible-extension' : 'below-threshold',
            suffix,
            willCompress,
          });
        }

        const nextStaged = recomputeDuplicates([...prev.staged, ...newEntries]);

        return {
          ...prev,
          rejectedNames: rejected,
          staged: nextStaged,
        };
      });
    },
    [disabled, allowedSet, nonCompressibleSet, autoCompressThresholdBytes, getDefaultSuffix]
  );

  const removeStaged = useCallback((id: string) => {
    setState((prev) => {
      const nextStaged = recomputeDuplicates(
        prev.staged.filter((stagedFile) => {
          return stagedFile.id !== id;
        })
      );

      return {
        ...prev,
        staged: nextStaged,
      };
    });
  }, []);

  const handleSuffixChange = useCallback((id: string, value: string) => {
    setState((prev) => {
      const nextStaged = recomputeDuplicates(
        prev.staged.map((stagedFile) => {
          return stagedFile.id === id ? { ...stagedFile, suffix: value } : stagedFile;
        })
      );

      return {
        ...prev,
        staged: nextStaged,
      };
    });
  }, []);

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isDefined(e.target.files) && e.target.files.length > 0) {
      stageFiles(e.target.files);
    }
    e.target.value = '';
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    if (!disabled && e.dataTransfer.files.length > 0) {
      stageFiles(e.dataTransfer.files);
    }
  };

  const handleUpload = useCallback(async () => {
    const { staged } = state;

    if (staged.length === 0 || disabled) {
      return;
    }

    // Block upload if any auto-suffixed file has a duplicate value
    if (
      staged.some(({ hasDuplicateSuffix, isCustomSuffix }) => {
        return !isCustomSuffix && hasDuplicateSuffix;
      })
    ) {
      return;
    }

    // Resolve all output filenames upfront and check for collisions before processing begins.
    // This catches duplicates in both the string and function template forms — including cases
    // where identical suffix values produce the same resolved name via the function form.
    if (isDefined(outputFilenameTemplate)) {
      const totalFiles = staged.length;
      const resolvedNames = staged.map((entry, i) => {
        return resolveFilename(outputFilenameTemplate, entry.suffix, i + 1, totalFiles, entry.file.name);
      });
      const uniqueNames = new Set(resolvedNames);

      if (uniqueNames.size !== resolvedNames.length) {
        patch({
          error: 'Two or more files would have the same output filename. Update the suffix values to make them unique.',
          status: 'error',
        });

        return;
      }
    }

    patch({ compressionSummary: null, error: null, processed: 0, progressMap: {}, status: 'compressing' });

    const controller = new AbortController();

    abortControllerRef.current = controller;

    const fileUploads: FileUpload[] = [];
    let totalOriginalBytes = 0;
    let totalFinalBytes = 0;
    let compressedCount = 0;
    const totalFiles = staged.length;

    try {
      for (let i = 0; i < staged.length; i++) {
        const entry = staged[i];

        if (isNotDefined(entry)) {
          continue;
        }

        const resolvedName = isDefined(outputFilenameTemplate)
          ? resolveFilename(outputFilenameTemplate, entry.suffix, i + 1, totalFiles, entry.file.name)
          : undefined;

        if (!entry.willCompress) {
          const finalFile = isDefined(resolvedName)
            ? new File([entry.file], resolvedName, { type: entry.file.type })
            : entry.file;

          fileUploads.push({
            file: finalFile,
            metaData: {
              fileName: finalFile.name,
              isCompressed: false,
              originalFileName: entry.file.name,
            },
          });

          totalOriginalBytes += entry.file.size;
          totalFinalBytes += finalFile.size;

          setState((prev) => {
            return {
              ...prev,
              progressMap: { ...prev.progressMap, [entry.id]: 100 },
            };
          });
          continue;
        }

        const result = await compressFile(entry.file, {
          onProgress: (ratio) => {
            setState((prev) => {
              return {
                ...prev,
                progressMap: {
                  ...prev.progressMap,
                  [entry.id]: Math.round(ratio * 100),
                },
              };
            });
          },
          outputFilename: resolvedName,
          signal: controller.signal,
        });

        fileUploads.push({
          file: result.file,
          metaData: {
            fileName: result.file.name,
            isCompressed: true,
            originalFileName: entry.file.name,
          },
        });

        totalOriginalBytes += result.originalSize;
        totalFinalBytes += result.compressedSize;
        compressedCount++;
      }

      const totalReductionPct =
        compressedCount > 0 && totalOriginalBytes > 0
          ? Number.parseFloat((((totalOriginalBytes - totalFinalBytes) / totalOriginalBytes) * 100).toFixed(1))
          : null;

      const compressionSummary: CompressionSummary = {
        compressedCount,
        totalFinalBytes,
        totalOriginalBytes,
        totalReductionPct,
      };

      patch({ compressionSummary, processed: fileUploads.length, status: 'done' });
      onFilesReady?.(fileUploads);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        patch({ error: null, progressMap: {}, status: 'cancelled' });
      } else {
        patch({
          error: err instanceof Error ? err.message : 'Processing failed',
          status: 'error',
        });
      }
    } finally {
      abortControllerRef.current = null;
    }
  }, [disabled, onFilesReady, outputFilenameTemplate, patch, state]);

  const cancelUpload = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  const { compressionSummary, error, processed, progressMap, rejectedNames, staged, status } = state;

  const isIdle = status === 'idle';
  const isCancelled = status === 'cancelled';
  const isCompressing = status === 'compressing';
  const isDone = status === 'done';
  const hasStaged = staged.length > 0;
  const dropZoneBlocked = disabled || isCompressing || isDone;

  const hasCompressibleFiles = staged.some(({ willCompress }) => {
    return willCompress;
  });

  const compressCount = staged.filter(({ willCompress }) => {
    return willCompress;
  }).length;
  const rawCount = staged.length - compressCount;

  const thresholdLabel = Number.isFinite(autoCompressThresholdBytes)
    ? `Auto-compresses files ≥ ${formatFileSize(autoCompressThresholdBytes)}`
    : 'Auto-compress disabled';

  // Upload blocked when any auto-suffixed file has a duplicate value
  const uploadBlocked = staged.some(({ hasDuplicateSuffix, isCustomSuffix }) => {
    return !isCustomSuffix && hasDuplicateSuffix;
  });

  return {
    acceptAttr,
    allowedSet,
    cancelUpload,
    compressCount,
    compressionSummary,
    dragging,
    dropZoneBlocked,
    error,
    handleSuffixChange,
    handleUpload,
    hasCompressibleFiles,
    hasStaged,
    inputRef,
    isCancelled,
    isCompressing,
    isDone,
    isIdle,
    onDrop,
    onInputChange,
    processed,
    progressMap,
    rawCount,
    rejectedNames,
    removeStaged,
    reset,
    setDragging,
    staged,
    status,
    thresholdLabel,
    uploadBlocked,
  };
};
